import React from 'react';
import { Link } from 'react-router-dom';
import { Volume2, Mic, Headphones, ArrowRightLeft, VolumeX, Rocket, AudioLines, CloudHail, AudioWaveform, FileText, Music, Mail, MapPin, Globe, Volume, Gauge, FileAudio } from 'lucide-react';
import { Logo } from './Logo';
import { AuthorBio } from '../Author/AuthorBio';

export function Footer() {
  const currentYear = new Date().getFullYear();

  const footerSections = {
    audioTesting: {
      title: 'Audio Testing Tools',
      items: [
        { label: 'Speaker Fix', href: '/', icon: Volume2 },
        { label: 'Speaker Frequency Test', href: '/speaker-frequency-test', icon: AudioWaveform },
        { label: 'Headphones & Earphones Test', href: '/headphones-earphones-test', icon: Headphones },
        { label: 'Left/Right Stereo Sound Test', href: '/left-right-stereo-sound-test', icon: ArrowRightLeft },
        { label: 'Microphone Tester', href: '/microphone-tester', icon: Mic },
        { label: 'Feedback Analyzer Test Online', href: '/feedback-analyzer', icon: VolumeX }
      ]
    },
    soundEnhancement: {
      title: 'Sound Enhancement',
      items: [
        { label: 'MP3 Volume Booster', href: '/mp3-volume-booster', icon: Rocket },
        { label: 'Equalizer Settings', href: '/equalizer-settings-online', icon: AudioLines },
        { label: 'Voice Changer', href: '/voice-changer', icon: Music },
        { label: 'Ambient Noise Cancellation Test', href: '/ambient-noise-cancellation-test', icon: CloudHail },
        { label: 'White Noise Generator', href: '/white-noise-and-pink-noise-generators', icon: AudioWaveform },
        { label: 'Pink Noise Generator', href: '/white-noise-and-pink-noise-generators', icon: AudioWaveform }
      ]
    },
    voiceTools: {
      title: 'Voice Tools',
      items: [
        { label: 'Audio to Text Converter', href: '/audio-to-text', icon: FileText },
        { label: 'Free Online Voice Recorder', href: '/online-voice-recorder', icon: Mic },
        { label: 'Decibel Meter', href: '/decibel-meter', icon: Volume },
        { label: 'Noise Dosimeter', href: '/noise-dosimeter', icon: Gauge },
        { label: 'Audio Format Converter', href: '/audio-format-converter', icon: FileAudio }
      ]
    },
    legal: {
      title: 'Legal',
      items: [
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Cookie Policy', href: '/cookies' },
        { label: 'GDPR Compliance', href: '/privacy#gdpr' }
      ]
    }
  };

  return (
    <footer className="relative mt-24 bg-gray-900">
      {/* Gradient divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      {/* Glow effect */}
      <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-1/3 h-24 bg-blue-500/10 blur-3xl" />

      <div className="relative bg-black/30 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Add Author Bio section before the footer links */}
          <div className="mb-12">
            <AuthorBio variant="compact" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Audio Testing Tools */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                {footerSections.audioTesting.title}
              </h3>
              <ul className="space-y-2">
                {footerSections.audioTesting.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      {item.icon && <item.icon className="w-4 h-4 group-hover:text-blue-400 transition-colors" />}
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sound Enhancement */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                {footerSections.soundEnhancement.title}
              </h3>
              <ul className="space-y-2">
                {footerSections.soundEnhancement.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      {item.icon && <item.icon className="w-4 h-4 group-hover:text-blue-400 transition-colors" />}
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Voice Tools */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                {footerSections.voiceTools.title}
              </h3>
              <ul className="space-y-2">
                {footerSections.voiceTools.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      {item.icon && <item.icon className="w-4 h-4 group-hover:text-blue-400 transition-colors" />}
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                {footerSections.legal.title}
              </h3>
              <ul className="space-y-2">
                {footerSections.legal.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-4">
                <Logo />
                <p className="text-sm text-gray-400">
                  Professional tools for device maintenance and repair.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-8">
                <Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">
                  About
                </Link>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Contact
                </Link>
                <Link to="/blog" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Blog
                </Link>
                <p className="text-sm text-gray-400">
                  © {currentYear} Fix Phone Speakers. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}